'use client';

import React, { useEffect, useRef } from 'react';

import { useTranslations } from 'next-intl';
import styled from 'styled-components';

import DownloadFilter from './DownloadFilter';
import DownloadHeader from './DownloadHeader';
import DownloadProduct from './DownloadProduct';
import { useShowOlderVersions } from './SelectedProductSlugsProvider';
import BreadcrumbsWrap from '@/ui/components/BreadcrumbsWrap';
import OutsideBox from '@/ui/containers/layout/OutsideBox';

const DownloadList = styled.div``;

interface DownloadsProps {
  products: Product[];
  downloadList: Record<string, DownloadListItem>;
  initialSlug?: string;
}

const Downloads: React.FC<DownloadsProps> = ({
  products,
  downloadList,
  initialSlug,
}) => {
  const t = useTranslations();
  const { selectedProductSlugs, setSelectedProductSlugs } =
    useShowOlderVersions();

  const prusaSlicerProduct: Product = {
    id: 0,
    title: 'PrusaSlicer',
    content: '',
    slug: 'prusaslicer',
    order: -1,
    count: 4,
    media: { image: {} },
    meta: { hp_visibility: false },
    original_id: 0,
    original_parent: 0,
    parent: 0,
    status: 'publish',
  };

  const updatedProducts = [prusaSlicerProduct, ...products];

  // Use a ref to ensure we only set the initial filter once.
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!isInitialized.current && initialSlug) {
      let newSlugs = [initialSlug];
      // If the initialSlug corresponds to a family product,
      // include its children so the expanded view renders.
      const matchingProduct = updatedProducts.find(
        product => product.slug === initialSlug
      );
      if (
        matchingProduct &&
        matchingProduct.childrens &&
        matchingProduct.childrens.length > 0
      ) {
        newSlugs = [
          initialSlug,
          ...matchingProduct.childrens.map(child => child.slug),
        ];
      }
      setSelectedProductSlugs(newSlugs);
      isInitialized.current = true;
    }
  }, [initialSlug, setSelectedProductSlugs, updatedProducts]);

  // Separate PrusaSlicer from other items
  const prusaSlicerItem = downloadList['prusaslicer'];
  const otherItems = Object.values(downloadList).filter(
    item => item.slug !== 'prusaslicer'
  );

  // Filtering for items based on the active filters.
  const filteredOtherItems =
    selectedProductSlugs.length > 0
      ? otherItems.filter(item => selectedProductSlugs.includes(item.slug))
      : otherItems;

  const finalList =
    !selectedProductSlugs.length && prusaSlicerItem
      ? [prusaSlicerItem, ...filteredOtherItems]
      : selectedProductSlugs.includes('prusaslicer')
        ? [prusaSlicerItem, ...filteredOtherItems]
        : filteredOtherItems;

  return (
    <>
      <BreadcrumbsWrap items={[{ name: t('downloads-title') }]} />
      <DownloadHeader title={t('downloads-title')} />
      <OutsideBox>
        <DownloadFilter products={updatedProducts} />
      </OutsideBox>

      <DownloadList>
        {finalList.map(item => (
          <DownloadProduct
            key={item.slug}
            fullWidth={item.slug === 'prusaslicer'}
            item={item}
          />
        ))}
      </DownloadList>
    </>
  );
};

export default Downloads;
